/**
 * FlowInput + FlowSegment — ported from the "OnFlow Design System" project
 * (v2/components/core/FlowInput.jsx).
 *
 * Fields are recessed rather than raised: an input reads as a hole in the
 * surface, which is the inverse of a card and makes a form scannable at a
 * glance. Focus lights the ember ring — ember is the action colour, and
 * typing is an action.
 */
import { useState } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';

interface FlowInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'style'> {
  label?: string;
  hint?: string;
  error?: string;
  suffix?: ReactNode;
  /** Display-type value input — used for the one number a sheet is about. */
  big?: boolean;
  style?: CSSProperties;
  wrapperStyle?: CSSProperties;
}

export function FlowInput({
  label,
  hint,
  error,
  suffix,
  big = false,
  style,
  wrapperStyle,
  ...rest
}: FlowInputProps) {
  const [focus, setFocus] = useState(false);
  const borderColor = error
    ? 'var(--off-track)'
    : focus
      ? 'color-mix(in srgb, var(--ember) 55%, transparent)'
      : 'var(--edge)';

  return (
    <label
      style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0, ...wrapperStyle }}
    >
      {label && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-label-size)',
            letterSpacing: 'var(--text-label-tracking)',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          {label}
        </span>
      )}
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--paper-0)',
          border: `1px solid ${borderColor}`,
          borderRadius: 'var(--r-sm)',
          padding: big ? '4px 14px' : '2px 14px',
          boxShadow: focus ? 'var(--ring-focus)' : 'inset 0 1px 3px rgba(0,0,0,.45)',
          transition:
            'box-shadow var(--t-state) var(--ease-out), border-color var(--t-state) var(--ease-out)',
        }}
      >
        <input
          {...rest}
          onFocus={(e) => {
            setFocus(true);
            rest.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocus(false);
            rest.onBlur?.(e);
          }}
          style={{
            flex: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            background: 'transparent',
            color: 'var(--ink-1)',
            fontFamily: big ? 'var(--font-display)' : 'var(--font-ui)',
            fontWeight: big ? 700 : 400,
            fontSize: big ? 26 : 15,
            letterSpacing: big ? '-0.02em' : 0,
            fontVariantNumeric: 'tabular-nums',
            padding: big ? '12px 0' : '13px 0',
            ...style,
          }}
        />
        {suffix && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              color: 'var(--ink-3)',
              flexShrink: 0,
            }}
          >
            {suffix}
          </span>
        )}
      </span>
      {(hint || error) && (
        <span style={{ fontSize: 12, color: error ? 'var(--off-track)' : 'var(--ink-3)' }}>
          {error || hint}
        </span>
      )}
    </label>
  );
}

export interface SegmentItem {
  id: string;
  label: ReactNode;
}

interface FlowSegmentProps {
  items: ReadonlyArray<SegmentItem | string>;
  active: string;
  onSelect?: (id: string) => void;
  style?: CSSProperties;
  'aria-label'?: string;
}

/**
 * Segmented control — the v2 replacement for underline tabs. The raised thumb
 * slides between cells so the active option reads as a physical position, not
 * a colour change.
 */
export function FlowSegment({
  items,
  active,
  onSelect,
  style,
  'aria-label': ariaLabel,
}: FlowSegmentProps) {
  const idx = Math.max(
    0,
    items.findIndex((i) => (typeof i === 'string' ? i : i.id) === active),
  );
  const share = 100 / Math.max(1, items.length);

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      style={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: `repeat(${items.length}, 1fr)`,
        background: 'var(--paper-0)',
        border: '1px solid var(--edge)',
        borderRadius: 'var(--r-sm)',
        padding: 3,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,.45)',
        ...style,
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 3,
          bottom: 3,
          left: `calc(${share}% * ${idx} + 3px)`,
          width: `calc(${share}% - 6px)`,
          background: 'var(--paper-3)',
          borderRadius: 'calc(var(--r-sm) - 3px)',
          boxShadow: 'var(--elev-1)',
          transition: 'left var(--t-state) var(--ease-out)',
        }}
      />
      {items.map((it) => {
        const id = typeof it === 'string' ? it : it.id;
        const label = typeof it === 'string' ? it : it.label;
        const on = id === active;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onSelect?.(id)}
            style={{
              position: 'relative',
              zIndex: 1,
              background: 'transparent',
              border: 0,
              padding: '9px 4px',
              fontFamily: 'var(--font-ui)',
              fontSize: 13,
              fontWeight: on ? 600 : 500,
              color: on ? 'var(--ink-1)' : 'var(--ink-3)',
              cursor: 'pointer',
              transition: 'color var(--t-state) var(--ease-out)',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
